import React, { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  IconButton,
  TextField,
  Paper,
  Chip,
  Tooltip,
  Typography,
  Fade,
} from '@mui/material'
import { styled, useTheme, alpha } from '@mui/material/styles'
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { setPendingFiles } from '../../../../redux/actions/conversationAction'
import { setSearchMode } from '../../../../redux/actions/userAction'
import ModeDropdown from '../../../../components/common/ModeDropdown'
import CompactCountryLanguageSelector from '../../../../components/common/CompactCountryLanguageSelector'

// Styled TextField matching old design
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '16px',
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '0px',
    '& fieldset': {
      borderColor: 'transparent',
      border: 'none'
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
      border: 'none'
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
      border: 'none',
      borderWidth: '0px',
    }
  }
}))

const MessageInput = ({
  value,
  onChange,
  onSubmit,
  onStop,
  isLoading,
  currentLang,
  textFocusRef,
  fileInputRef: externalFileInputRef,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const localFileInputRef = useRef()
  const fileInputRef = externalFileInputRef || localFileInputRef

  // Redux state
  const selectedFiles = useSelector(state => state.conversations.pendingFiles) || []
  const mode = useSelector(state => state.user?.searchMode) || 'law'

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!isLoading && value.trim()) onSubmit()
    }
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return
    const MAX_FILES = 5
    const MAX_SIZE = 10 * 1024 * 1024 // 10MB

    if (files.length > MAX_FILES) {
      alert(t('max_files_limit', { count: MAX_FILES }))
      return
    }

    const validFiles = files.filter(f => f.size <= MAX_SIZE)
    if (validFiles.length !== files.length) {
      alert(t('file_size_limit_exceeded', { size: 10 }))
    }

    dispatch(setPendingFiles(validFiles))
    // Reset input so the same file can be re-selected in a new conversation
    e.target.value = ''
  }

  const removeFile = (index) => {
    dispatch(setPendingFiles(selectedFiles.filter((_, i) => i !== index)))
  }

  const handleModeSelect = (e, newMode) => {
    dispatch(setSearchMode(newMode))
  }

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 8px rgba(0, 0, 0, 0.3)'
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:focus-within': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`,
        }
      }}
    >
      {/* File Upload Chips */}
      {selectedFiles?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            height: 'auto',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.text.disabled,
              borderRadius: '4px',
            }
          }}
        >
          <Fade in={true}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                overflow: 'auto',
                backgroundColor: 'transparent'
              }}
            >
              {selectedFiles?.map((file, index) => (
                <Chip
                  key={index}
                  icon={<DescriptionIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />}
                  label={
                    <Tooltip title={file.name} arrow placement='top'>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: '100px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500
                        }}
                      >
                        {file.name}
                      </Typography>
                    </Tooltip>
                  }
                  onDelete={() => removeFile(index)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    mx: 0.5,
                    backgroundColor: theme.palette.action.hover,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '6px',
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.error.light,
                      '&:hover': {
                        color: theme.palette.error.main
                      }
                    }
                  }}
                />
              ))}
            </Box>
          </Fade>
        </Box>
      )}

      {/* Text Input Field */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          p: { xs: '8px 8px', sm: '12px 8px' },
          backgroundColor: theme.palette.background.paper
        }}
      >
        <StyledTextField
          className='message-input'
          fullWidth
          variant='outlined'
          placeholder={t('how_can_i_help') || 'How can I help you?'}
          multiline
          disabled={isLoading}
          minRows={2}
          maxRows={6}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          inputRef={textFocusRef}
          dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& input, & textarea': {
                fontSize: '16px'
              }
            }
          }}
        />
      </Box>

      {/* Bottom Controls Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: '6px',
          backgroundColor: theme.palette.background.paper,
          gap: 0.5,
        }}
      >
        {/* Left side - Upload, Country/Language, Mode */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {/* Upload Button */}
          <IconButton
            data-tour="chat-upload-btn"
            disabled={isLoading}
            onClick={() => fileInputRef.current.click()}
            sx={{
              color: isLoading ? theme.palette.text.disabled : theme.palette.primary.main,
              backgroundColor: 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              },
              p: 1
            }}
            size="small"
            title="Upload file"
          >
            <AttachFileIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Country + jurisdiction selector (shared with search) */}
          <Box data-tour="chat-country-selector" sx={{ display: 'inline-flex', minWidth: 0 }}>
            <CompactCountryLanguageSelector displayMode="icon" disabled={isLoading} />
          </Box>

          {/* Mode Selector (multi-select icon mode) */}
          <Box data-tour="chat-mode-selector" sx={{ display: 'inline-flex' }}>
            <ModeDropdown
              value={mode}
              onChange={handleModeSelect}
              displayMode="icon"
              multiSelect
              disabled={isLoading}
            />
          </Box>
        </Box>

        {/* Right side - Send/Stop Button */}
        <Box>
          {isLoading ? (
            <IconButton
              data-tour="chat-send-btn"
              onClick={onStop}
              sx={{
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.12)
                },
                p: 1
              }}
              size="small"
              title="Stop"
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: theme.palette.error.main,
                  borderRadius: '2px'
                }}
              />
            </IconButton>
          ) : (
            <IconButton
              data-tour="chat-send-btn"
              onClick={onSubmit}
              disabled={!value.trim()}
              sx={{
                color: !value.trim() ? theme.palette.text.disabled : theme.palette.primary.main,
                backgroundColor: !value.trim() ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: !value.trim() ? 'transparent' : alpha(theme.palette.primary.main, 0.12)
                },
                p: 1
              }}
              size="small"
              title="Send"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor"/>
              </svg>
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Hidden file input */}
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept='.pdf,.txt,.docx,.pptx,.xlsx,.csv,application/pdf,text/plain,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        multiple
      />
    </Paper>
  )
}

export default MessageInput
